import { CheckCircle } from "lucide-react";

export const CheckList = ({ items }) => {
    return (
        <ul className="space-y-3">
            {items.map((item) => (
                <li key={item} className="flex gap-3">
                    <CheckCircle
                        className="mt-1 shrink-0 text-green-600"
                        size={20}
                        strokeWidth={3}
                    />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
};

export const SectionCard = ({
    icon: Icon,
    title,
    eyebrow,
    children,
    color = "bg-[#ffbc00]",
    rotate = "",
}) => {
    return (
        <section
            className={`rounded-[22px] border-[4px] border-black bg-white p-5 shadow-[7px_7px_0_#000] ${rotate}`}
        >
            <div className="mb-4 flex items-center gap-3">
                <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-black ${color} shadow-[3px_3px_0_#000]`}
                >
                    <Icon size={24} strokeWidth={3} />
                </div>

                <div>
                    {eyebrow && (
                        <p className="text-xs font-black uppercase tracking-wide text-red-500">
                            {eyebrow}
                        </p>
                    )}
                    <h2 className="text-xl font-black uppercase leading-tight md:text-2xl">
                        {title}
                    </h2>
                </div>
            </div>

            <div className="text-sm font-bold leading-7 md:text-base">
                {children}
            </div>
        </section>
    );
};

export const SayThisBox = ({ children }) => {
    return (
        <div className="mt-3 rounded-[16px] border-[3px] border-black bg-[#f8f4ea] p-4 shadow-[4px_4px_0_#000]">
            <p className="mb-1 text-xs font-black uppercase text-red-500">
                Say this
            </p>
            <p className="font-bold leading-7">{children}</p>
        </div>
    );
};

export const WideIntroCard = ({ icon: Icon, title, children, color = "bg-[#ffbc00]" }) => {
    return (
        <section className="relative overflow-hidden rounded-[28px] border-[5px] border-black bg-white p-6 shadow-[9px_9px_0_#000] md:p-8">
            <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full border-[4px] border-black bg-[#ffbc00]/30" />

            <div className="relative flex flex-col gap-5 md:flex-row md:items-start">
                <div
                    className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-[4px] border-black ${color} shadow-[4px_4px_0_#000]`}
                >
                    <Icon size={32} strokeWidth={3} />
                </div>

                <div>
                    <h2 className="mb-3 text-3xl font-black uppercase leading-tight">
                        {title}
                    </h2>

                    <div className="text-sm font-bold leading-7 md:text-base">
                        {children}
                    </div>
                </div>
            </div>
        </section>
    );
};

export const ChecklistStrip = ({ icon: Icon, title, items, color = "bg-[#22c55e]" }) => {
    return (
        <section className="overflow-hidden rounded-[20px] border-[4px] border-black bg-white shadow-none md:shadow-[7px_7px_0_#000]">
            <div className={`${color} flex items-center gap-3 rounded-t-[12px] border-b-[4px] border-black px-5 py-3`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-black bg-white">
                    <Icon size={22} strokeWidth={3} />
                </div>

                <h2 className="text-xl font-black uppercase md:text-2xl">
                    {title}
                </h2>
            </div>

            <div className="p-4 font-semibold sm:p-5">
                <CheckList items={items} />
            </div>
        </section>
    );
};

export const SayThisCard = ({ icon: Icon, title, children, color = "bg-[#16bdf4]" }) => {
    return (
        <section className="rounded-[22px] border-[4px] border-black bg-[#f8f4ea] p-5 shadow-[7px_7px_0_#000]">
            <div className="mb-4 flex items-center gap-3">
                <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-black ${color} shadow-[3px_3px_0_#000]`}
                >
                    <Icon size={24} strokeWidth={3} />
                </div>

                <div>
                    <p className="text-xs font-black uppercase tracking-wide text-red-500">
                        Say this
                    </p>
                    <h2 className="text-xl font-black uppercase leading-tight md:text-2xl">
                        {title}
                    </h2>
                </div>
            </div>

            <div className="rounded-[16px] border-[3px] border-black bg-white p-4">
                <p className="text-sm font-semibold leading-7 md:text-base">
                    {children}
                </p>
            </div>
        </section>
    );
};

export const SupportCard = ({ icon: Icon, title, children, tips, color = "bg-[#ffbc00]" }) => {
    return (
        <section className="rounded-[26px] border-[5px] border-black bg-[#fff7cc] p-5 shadow-[0px_0px_0_#000] md:shadow-[9px_9px_0_#000] md:p-6">
            <div className="grid gap-5 md:grid-cols-[220px_1fr] md:items-center">
                <div className="flex flex-col items-start">
                    <div
                        className={`mb-4 flex h-14 w-14 items-center justify-center rounded-full border-[3px] border-black ${color} shadow-[4px_4px_0_#000]`}
                    >
                        <Icon size={28} strokeWidth={3} />
                    </div>

                    <h2 className="text-2xl font-black uppercase leading-tight">
                        {title}
                    </h2>
                </div>

                <div>
                    <SayThisBox>{children}</SayThisBox>

                    {tips && (
                        <div className="mt-4 rounded-[16px] border-[3px] border-black bg-white p-4">
                            <CheckList items={tips} />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};